import moment from 'moment';

/* The core rule of the whole file (per the top comment): the window to submit preferences for month M lives in month M-1 */

//It just caps a day-of-month number at however many days that particular month actually has.

/*
Examples:
- clampDayToMonth(15, <any month>) → 15 (no clamping needed, every month has ≥15 days)
- clampDayToMonth(31, <February, non-leap>) → 28
- clampDayToMonth(31, <February, leap year>) → 29
- clampDayToMonth(31, <April>) → 30
- clampDayToMonth(31, <January>) → 31 (January actually has 31, so no clamp applied)
*/
const clampDayToMonth = (day, monthMoment) => Math.min(day, monthMoment.daysInMonth());

export const AvailabilityWindowStatus = {
  LOCKED: 'locked',
  OPEN: 'open',
  NOT_OPEN_YET: 'not_open_yet'
};

// 1. windowMonth = the month before targetMonth (any date within M works as input — only year/month is read; .startOf('month') normalizes it, then .subtract(1, 'month') steps back).
// 2. opensAt = windowMonth's startDay, clamped to that month's length, at 00:00:00.000 UTC.
// 3. closesAt = windowMonth's endDay, clamped, at 23:59:59.999 UTC (.endOf('day')) — so the window is inclusive of the entire closing day.

// Examples:

// getWindowBounds(1, 15, <any date in October 2026>)
//    → windowMonth = September 2026
//    → opensAt = 2026-09-01T00:00:00.000Z, closesAt = 2026-09-15T23:59:59.999Z
//    → October's preferences can be submitted Sept 1–15; from Sept 16 through end of October, October is locked.
const getWindowBounds = (startDay, endDay, targetMonth) => {
  const windowMonth = moment.utc(targetMonth).startOf('month').subtract(1, 'month');

  const opensAt = windowMonth.clone().date(clampDayToMonth(startDay, windowMonth)).startOf('day');
  const closesAt = windowMonth.clone().date(clampDayToMonth(endDay, windowMonth)).endOf('day');

  return { opensAt, closesAt };
};

// Resolves a single arbitrary target month/date's (any date within month M) locked/open/not_open_yet
// status, as of `referenceDate` (defaults to now).
export const resolveAvailabilityWindowForMonth = ({ startDay, endDay, targetMonth, referenceDate = new Date() }) => {
  const ref = moment.utc(referenceDate);
  const month = moment.utc(targetMonth).format('YYYY-MM');

  const { opensAt, closesAt } = getWindowBounds(startDay, endDay, targetMonth);

  if (ref.isBefore(opensAt)) {
    return { month, status: AvailabilityWindowStatus.NOT_OPEN_YET, opensAt: opensAt.toDate() };
  }

  if (!ref.isAfter(closesAt)) {
    return { month, status: AvailabilityWindowStatus.OPEN, closesAt: closesAt.toDate() };
  }

  return { month, status: AvailabilityWindowStatus.LOCKED, closedAt: closesAt.toDate() };
};

// Returns 3 rows for display: the current calendar month (always 'locked' - its own window, the
// month before it, has already passed by construction), the next calendar month, and the one
// after that. Used by the settings panel's "what employees see right now" preview.
export const resolveAvailabilityWindowPreview = ({ startDay, endDay, referenceDate = new Date() }) => {
  if (!startDay || !endDay) {
    return [];
  }

  const currentMonth = moment.utc(referenceDate).startOf('month');

  return [0, 1, 2].map((offset) =>
    resolveAvailabilityWindowForMonth({
      startDay,
      endDay,
      targetMonth: currentMonth.clone().add(offset, 'month').toDate(),
      referenceDate
    })
  );
};
